<template>
  <div>
    <ca-heading text="Cadastro de lotação" />

    <ca-page>
      <ca-loader v-if="showLoading"></ca-loader>

      <ca-form
        v-else
        :key="formKey"
        :is-valid.sync="isFormValid"
        :on-submit="onSubmit"
        :on-submit-success="onSubmitSuccess"
        :on-submit-error="onSubmitError"
        :error-message="errorMessage">
        <ca-row>
          <ca-col size="6">
            <ca-field label="Código">
              <stock-code-input
                v-model="stock.allocationCode"
                :disabled="hasStockId"
                maxlength="30"
                @change="onChangeStockCode">
              </stock-code-input>
            </ca-field>
          </ca-col>

          <ca-col size="6">
            <ca-field
              label="Tipo de Lotação"
              icon-tooltip="O cadastro de lotação tem conceito estritamente tributário, seu cadastro
              influencia no método de cálculo da contribuição previdenciária. Não se confunde, com o
              local de trabalho do funcionário.">
              <ca-search-select
                v-model="stock.allocationType.name"
                required>
                <ca-option
                  v-for="option in misc.stockingTypes"
                  :key="option.value"
                  :value="option.name">
                  {{ option.description }}
                </ca-option>
              </ca-search-select>
            </ca-field>
          </ca-col>

          <ca-col size="6">
            <ca-field
              label="Código FPAS"
              icon-tooltip="Código do Fundo da Previdência
              e Assistência Social para ser enviado à SEFIP e ao eSocial.">
              <ca-input
                v-model="stock.fpasCode"
                v-mask="'###'"
                required>
              </ca-input>
            </ca-field>
          </ca-col>
          <ca-col size="6">
            <ca-field
              label="Código de Terceiros"
              icon-tooltip="Informar o código na Tabela de códigos de
              Outras Entidades (Terceiros) que será usado para enviar a
              SEFIP e calcular as alíquotas de terceiros. Se não houver preencha com zeros.">
              <ca-input
                v-model="stock.thirdPartyCode"
                v-mask="'####'"
                :custom-validations="thirdPartyCodeValidation"
                required></ca-input>
            </ca-field>
          </ca-col>

          <ca-col v-if="hasInscription(stockType)" size="6">
            <ca-field :label="getInscriptionLabel(stockType)">
              <inscription-input :stock-type="stockType" :stock="stock"></inscription-input>
            </ca-field>
          </ca-col>
        </ca-row>

        <ca-row v-if="showCnoInputs">
          <ca-col size="6">
            <ca-field label="Tipo de inscrição do contratante">
              <ca-select
                v-model="stock.contractRegistrationType.name"
                required>
                <ca-option
                  v-for="option in misc.companyInscriptionType"
                  :key="option.name"
                  :value="option.value">
                  {{ option.description }}
                </ca-option>
              </ca-select>
            </ca-field>
          </ca-col>
          <ca-col size="6">
            <ca-field
              label="Inscrição do contratante">
              <cnpj-input
                v-if="stock.contractRegistrationType.name === LEGAL_INSCRIPTION_VALUE"
                v-model="stock.contractRegistration"
                maxlength="45"
                required></cnpj-input>
              <ca-cpf-input
                v-else
                v-model="stock.contractRegistration"
                maxlength="45"
                required></ca-cpf-input>
            </ca-field>
          </ca-col>
          <ca-col size="6">
            <ca-field label="Tipo de inscrição do CNO">
              <ca-select
                v-model="stock.cnoContractRegistrationType.name"
                required>
                <ca-option
                  v-for="option in misc.companyInscriptionType"
                  :key="option.name"
                  :value="option.value">
                  {{ option.description }}
                </ca-option>
              </ca-select>
            </ca-field>
          </ca-col>
          <ca-col size="6">
            <ca-field
              label="Inscrição do CNO">
              <cnpj-input
                v-if="stock.cnoContractRegistrationType.name === LEGAL_INSCRIPTION_VALUE"
                v-model="stock.cnoContractRegistration"
                maxlength="45"
                required></cnpj-input>
              <ca-cpf-input
                v-else
                v-model="stock.cnoContractRegistration"
                maxlength="45"
                required></ca-cpf-input>
            </ca-field>
          </ca-col>
        </ca-row>

        <ca-row>
          <ca-col size="6">
            <ca-field label="Situação no eSocial">
              <ca-select
                v-model="stock.statusEsocial.name"
                :on-change="onChangeStatusESocial"
                @change="refreshForm">
                <ca-option
                  v-for="status in misc.esocialStatus.default"
                  :key="status.name"
                  :value="status.value">
                  {{ status.description }}
                </ca-option>
              </ca-select>
            </ca-field>
          </ca-col>
          <ca-col v-if="requireESocialChangeDate" size="3">
            <ca-field label="Data de alteração">
              <ca-date-input
                v-model="stock.esocialChangeDate"
                :required="requireESocialChangeDate">
              </ca-date-input>
            </ca-field>
          </ca-col>
        </ca-row>

        <ca-footer>
          <ca-row vertical-offset="2">
            <ca-col size="2">
              <ca-button full-width @click="goBack">
                Voltar
              </ca-button>
            </ca-col>
            <ca-col offset="8" size="2">
              <ca-submit-button>
                Salvar
              </ca-submit-button>
            </ca-col>
          </ca-row>
        </ca-footer>
      </ca-form>
    </ca-page>
  </div>
</template>

<script>
import { initFormObject, getBoxInformationErrorMessage, getErrorMessageFromResponse } from 'packages/common/utils';
import StockCodeInput from './components/StockCodeInput';
import InscriptionInput from './components/InscriptionInput';
import {
  getStock, createStock, editStock, hasInscription, getInscriptionLabel,
} from './stockingService';
import { STOCKING_TYPES, LEGAL_INSCRIPTION_VALUE } from './constants';

export default {
  name: 'StockingsFormPage',
  components: {
    StockCodeInput,
    InscriptionInput,
  },
  props: {
    isModal: Boolean,
    isValid: Boolean,
    object: Object,
  },
  data() {
    return {
      formKey: 0,
      isFormValid: false,
      stockId: this.$route.params.id,
      stock: {
        ...initFormObject([
          { statusEsocial: 'N' },
          { allocationType: 'O01' },
          { contractRegistrationType: 'J' },
          { cnoContractRegistrationType: 'J' },
        ]),
        allocationCode: null,
        registration: null,
        contractRegistration: null,
        cnoContractRegistration: null,
      },
      existentCodes: [],
      thirdPartyCodeValidation: [{
        message: 'O código precisa ter 4 caracteres.',
        valid: value => {
          return value.length === 4;
        },
      }],
      errorMessage: null,
      showLoading: false,
      LEGAL_INSCRIPTION_VALUE,
    };
  },
  computed: {
    hasStockId() {
      return !!this.stockId;
    },
    misc() {
      return this.$store.state.misc;
    },
    stockType() {
      return this.stock.allocationType.name;
    },
    showCnoInputs() {
      return this.stockType === STOCKING_TYPES.CNO;
    },
    requireESocialChangeDate() {
      return this.stock.statusEsocial.name === 'A';
    },
  },
  watch: {
    isFormValid(isValid) {
      if (this.isModal) {
        this.$emit('update:isValid', isValid);
      }
    },
    stock(value) {
      if (this.isModal) {
        this.$emit('update:object', value);
      }
    },
    stockType() {
      this.refreshForm();
    },
    'stock.cnoContractRegistrationType.name': function fn() {
      this.refreshForm();
    },
  },
  created() {
    this.initData();
  },
  methods: {
    hasInscription,
    getInscriptionLabel,
    initData() {
      this.$store.dispatch('misc/getEsocialStatus');
      this.$store.dispatch('misc/getStockingTypes');
      this.$store.dispatch('misc/getCompanyInscriptionType', 'EMPRESAS');

      if (this.hasStockId && !this.isModal) {
        this.setStock();
      }
    },
    async setStock() {
      this.showLoading = true;
      try {
        this.stock = await getStock(this.stockId);
      } catch (error) {
        this.$toaster.error(getErrorMessageFromResponse(error));
      } finally {
        this.showLoading = false;
      }
      this.onChangeStatusESocial();
    },
    onChangeStockCode(value) {
      this.stock.allocationCode = value;
    },
    goBack() {
      this.$router.back();
    },
    onSubmit() {
      return this.hasStockId
        ? editStock(this.stockId, this.stock)
        : createStock(this.stock);
    },
    onSubmitSuccess() {
      this.goBack();
      this.$toaster.success('Dados salvos com sucesso');
    },
    onSubmitError(error) {
      this.errorMessage = getBoxInformationErrorMessage(error);
    },
    refreshForm() {
      this.formKey++;
    },
    onChangeStatusESocial() {
      if (!this.requireESocialChangeDate) {
        this.$set(this.stock, 'esocialChangeDate', null);
      }
    },
  },
};
</script>
